import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function HomePage() {
  // Loan Calculator State
  const [loanAmount, setLoanAmount] = useState(1000);
  const [loanTerm, setLoanTerm] = useState(6);
  const [interestRate] = useState(10); // 10% monthly rate as example

  const calculateMonthlyPayment = () => {
    const principal = loanAmount;
    const monthlyRate = interestRate / 100;
    const totalInterest = principal * monthlyRate * loanTerm;
    const totalAmount = principal + totalInterest;
    const monthlyPayment = totalAmount / loanTerm;
    return {
      monthlyPayment: monthlyPayment.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      totalAmount: totalAmount.toFixed(2)
    };
  };

  const calculatorResults = calculateMonthlyPayment();

  const loanProducts = [
    {
      icon: 'ri-user-line',
      title: 'Salary-Based Loans',
      description: 'Flexible financing for salaried employees with transparent terms and competitive rates.',
      link: '/loans#salary'
    },
    {
      icon: 'ri-briefcase-line',
      title: 'SME & Working Capital',
      description: 'Empower your business growth with tailored working capital solutions.',
      link: '/loans#sme'
    },
    {
      icon: 'ri-store-line',
      title: 'Microenterprise Loans',
      description: 'Supporting small entrepreneurs and informal businesses to thrive and expand.',
      link: '/loans#microenterprise'
    },
    {
      icon: 'ri-safe-line',
      title: 'Asset-Backed Loans',
      description: 'Secure financing backed by your valuable assets with favorable terms.',
      link: '/loans#asset'
    }
  ];

  const whyChoose = [
    {
      icon: 'ri-shield-check-line',
      title: 'RBZ Licensed & Regulated',
      description: 'Fully compliant with Reserve Bank of Zimbabwe regulations and standards.'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Transparent Lending',
      description: 'Clear terms, no hidden fees, and straightforward loan agreements.'
    },
    {
      icon: 'ri-time-line',
      title: 'Responsible Approvals',
      description: 'Fast processing with thorough assessment to ensure sustainable lending.'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Customer-First Approach',
      description: 'Dedicated support team committed to your financial success and wellbeing.'
    }
  ];

  const trustIndicators = [
    { icon: 'ri-shield-star-line', text: 'RBZ Licensed' },
    { icon: 'ri-calendar-line', text: '10+ Years Experience' },
    { icon: 'ri-eye-line', text: 'Transparent Terms' },
    { icon: 'ri-map-pin-line', text: 'Local Expertise' }
  ];

  const testimonials = [
    {
      name: 'Tendai Moyo',
      role: 'Small Business Owner',
      location: 'Harare',
      image: 'https://public.readdy.ai/ai/img_res/b28954c557d7d39965fa44bc49029ce8.jpg',
      quote: 'Nakhalat Finance helped me expand my grocery store when traditional banks turned me away. The process was straightforward, and the team genuinely cared about my business success.',
      loanType: 'SME Loan',
      rating: 5
    },
    {
      name: 'Grace Chikwanha',
      role: 'Teacher',
      location: 'Bulawayo',
      image: 'https://readdy.ai/api/search-image?query=Professional%20African%20woman%20teacher%20in%20her%2030s%20wearing%20elegant%20blouse%20with%20warm%20genuine%20smile%20soft%20natural%20lighting%20clean%20neutral%20background%20portrait%20style&width=200&height=200&seq=testimonial2&orientation=squarish',
      quote: 'When I needed funds for my daughter\'s university fees, Nakhalat provided a salary-based loan within 48 hours. Their transparent terms gave me peace of mind.',
      loanType: 'Salary-Based Loan',
      rating: 5
    },
    {
      name: 'Blessing Ncube',
      role: 'Market Vendor',
      location: 'Harare',
      image: 'https://readdy.ai/api/search-image?query=African%20woman%20entrepreneur%20in%20her%2050s%20wearing%20colorful%20traditional%20attire%20with%20proud%20confident%20expression%20warm%20lighting%20simple%20clean%20background%20portrait&width=200&height=200&seq=testimonial3&orientation=squarish',
      quote: 'As a market vendor, I never thought I could access formal financing. Nakhalat\'s microenterprise loan helped me buy more stock and double my income.',
      loanType: 'Microenterprise Loan',
      rating: 5
    },
    {
      name: 'Tatenda Zimuto',
      role: 'Transport Operator',
      location: 'Mutare',
      image: 'https://readdy.ai/api/search-image?query=African%20man%20in%20his%2030s%20wearing%20casual%20polo%20shirt%20with%20friendly%20approachable%20smile%20natural%20daylight%20clean%20simple%20background%20professional%20portrait&width=200&height=200&seq=testimonial4&orientation=squarish',
      quote: 'I used an asset-backed loan to purchase a second kombi for my transport business. The flexible repayment schedule made it easy to manage alongside my daily operations.',
      loanType: 'Asset-Backed Loan',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#E8EFF5] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A3A52] leading-tight">
                Nakhalat Microfinance Zimbabwe
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                Nakhalat Finance (Private) Limited is a licensed microfinance institution providing transparent, affordable loans to individuals and small businesses across Zimbabwe.
              </p>
              
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-3 bg-white px-5 py-3 rounded-lg border border-gray-200 shadow-sm">
                <i className="ri-shield-check-line text-2xl text-[#14B8A6]"></i>
                <span className="text-sm font-medium text-gray-700">
                  Licensed & Regulated by the Reserve Bank of Zimbabwe
                </span>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/apply">
                  <Button variant="primary" size="large" icon={<i className="ri-arrow-right-line"></i>}>
                    Apply for a Loan
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="secondary" size="large">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/e4c5981b2fc547acaa3ea617440dd341/31da31a34500fc79941e0da748dd209e.jpeg"
                  alt="Nakhalat Finance serving Zimbabwe businesses"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Bar */}
      <section className="py-8 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {trustIndicators.map((indicator, index) => (
              <div key={index} className="flex items-center justify-center gap-3">
                <i className={`${indicator.icon} text-2xl text-[#1A3A52]`}></i>
                <span className="text-sm font-medium text-gray-700">{indicator.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Loan Products Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-4">
              Our Loan Solutions
            </h2>
            <p className="text-lg text-gray-600">
              Tailored financial products for your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {loanProducts.map((product, index) => (
              <Link 
                key={index} 
                to={product.link}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300 cursor-pointer group"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#E8EFF5] rounded-lg mb-4 group-hover:bg-[#1A3A52] transition-colors">
                  <i className={`${product.icon} text-2xl text-[#1A3A52] group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#1A3A52] mb-2">
                  {product.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  {product.description}
                </p>
                <div className="flex items-center text-[#1A3A52] text-sm font-medium group-hover:gap-2 transition-all">
                  Learn More
                  <i className="ri-arrow-right-line ml-1 group-hover:ml-0"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Loan Calculator Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1A3A52] to-[#2D5A7B]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-white/10 text-white text-sm font-medium rounded-full mb-4">
              Plan Your Finances
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Loan Calculator
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Estimate your monthly repayments and plan your budget with confidence
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Calculator Inputs */}
              <div className="p-8 lg:p-10 space-y-8">
                {/* Loan Amount */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-semibold text-[#1A3A52]">
                      Loan Amount
                    </label>
                    <span className="text-lg font-bold text-[#1A3A52]">
                      ${loanAmount.toLocaleString()}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="100"
                    max="10000"
                    step="100"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#14B8A6]"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>$100</span>
                    <span>$10,000</span>
                  </div>
                </div>

                {/* Loan Term */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-semibold text-[#1A3A52]">
                      Loan Term
                    </label>
                    <span className="text-lg font-bold text-[#1A3A52]">
                      {loanTerm} {loanTerm === 1 ? 'Month' : 'Months'}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="24"
                    step="1"
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#14B8A6]"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>1 Month</span>
                    <span>24 Months</span>
                  </div>
                </div>

                {/* Interest Rate Display */}
                <div className="bg-[#E8EFF5] rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-percent-line text-[#1A3A52]"></i>
                      <span className="text-sm text-gray-600">Interest Rate</span>
                    </div>
                    <span className="text-sm font-semibold text-[#1A3A52]">
                      {interestRate}% per month
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  <i className="ri-information-line mr-1"></i>
                  This calculator provides estimates only. Actual rates may vary based on your profile and loan type.
                </p>
              </div>

              {/* Results Panel */}
              <div className="bg-[#1A3A52] p-8 lg:p-10 flex flex-col justify-center">
                <div className="space-y-6">
                  <div className="text-center">
                    <p className="text-white/60 text-sm mb-2">Your Estimated Monthly Payment</p>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl md:text-6xl font-bold text-white">
                        ${calculatorResults.monthlyPayment}
                      </span>
                      <span className="text-white/60 text-lg">/month</span>
                    </div>
                  </div>

                  <div className="border-t border-white/20 pt-6 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-white/70 text-sm">Principal Amount</span>
                      <span className="text-white font-semibold">${loanAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/70 text-sm">Total Interest</span>
                      <span className="text-white font-semibold">${calculatorResults.totalInterest}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-white/20">
                      <span className="text-white/70 text-sm">Total Repayment</span>
                      <span className="text-[#14B8A6] font-bold text-lg">${calculatorResults.totalAmount}</span>
                    </div>
                  </div>

                  <Link to="/apply" className="block">
                    <Button 
                      variant="secondary" 
                      size="large" 
                      className="w-full !bg-[#14B8A6] !text-white hover:!bg-[#0D9488] !border-0"
                      icon={<i className="ri-arrow-right-line text-white"></i>}
                    >
                      Apply for This Loan
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Nakhalat */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#E8EFF5]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 gap-12">
            {/* Benefits */}
            <div className="space-y-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-8">
                Why Choose Nakhalat Finance
              </h2>
              {whyChoose.map((benefit, index) => (
                <div key={index} className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1A3A52] rounded-full flex-shrink-0">
                    <i className={`${benefit.icon} text-xl text-white`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1A3A52] mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-[#E8EFF5] text-[#1A3A52] text-sm font-medium rounded-full mb-4">
              Success Stories
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real stories from Zimbabweans who achieved their goals with Nakhalat Finance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-[#F8FAFB] to-white rounded-2xl p-8 border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                  ))}
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-6 text-base italic">
                  "{testimonial.quote}"
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#14B8A6]">
                      <img 
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1A3A52]">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {testimonial.role}, {testimonial.location}
                      </p>
                    </div>
                  </div>
                  <span className="px-3 py-1.5 bg-[#E8EFF5] text-[#1A3A52] text-xs font-medium rounded-full whitespace-nowrap">
                    {testimonial.loanType}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/apply">
              <Button variant="primary" icon={<i className="ri-arrow-right-line"></i>}>
                Start Your Success Story
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#1A3A52]">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Ready to Take the Next Step?
          </h2>
          <p className="text-lg text-white/80 leading-relaxed">
            Our team is here to guide you through a simple, transparent application process
          </p>
          <Link to="/apply">
            <Button 
              variant="secondary" 
              size="large" 
              className="bg-white text-[#1A3A52] hover:bg-gray-100 border-0"
              icon={<i className="ri-arrow-right-line"></i>}
            >
              Start Your Application
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
